/**
 * TooltipFormatter - Tooltip Value Formatting Service
 * 
 * SOLID Principle: Single Responsibility Principle (SRP)
 * Purpose: This service is solely responsible for formatting sensor values
 * for display in chart tooltips. It handles field-specific formatting rules,
 * units, precision, and special cases without any knowledge of UI rendering.
 * 
 * Responsibilities:
 * - Format numeric values according to field type
 * - Handle special cases (CO2 sensor modes, invalid data, etc.)
 * - Apply appropriate precision and units
 * - Generate human-readable labels
 * 
 * Context: Tooltips show when users hover over chart data points. Different
 * sensor types need different formatting - temperature to 2 decimals with °C,
 * pressure to 2 decimals with dbar, etc.
 */

import { FieldTypeRegistry } from '../FieldTypeRegistry.js';
import { LabelUtils } from '../LabelUtils.js';

export class TooltipFormatter {
  /**
   * Main tooltip formatting function that delegates to field-specific formatters
   * 
   * @param {number|null|undefined} value - Raw sensor value to format
   * @param {string} fieldKey - Field identifier (measurement:field)
   * @param {Object} payload - Complete data point with metadata
   * @returns {Array} [formattedValue, fieldLabel] tuple for tooltip display
   */
  static formatTooltipValue(value, fieldKey, payload = {}) {
    // Handle missing or invalid values consistently
    if (value === null || value === undefined || isNaN(value)) {
      return ['No data', LabelUtils.getFieldLabel(fieldKey)];
    }

    // Get field type configuration for formatting rules
    const fieldType = FieldTypeRegistry.getFieldType(fieldKey);
    const fieldLabel = LabelUtils.getFieldLabel(fieldKey);

    // Check for special formatting cases first
    const specialFormatter = this._getSpecialFormatter(fieldKey);
    if (specialFormatter) {
      return [specialFormatter(value, payload, fieldType), fieldLabel];
    }

    // Use standard field type formatting
    const formattedValue = fieldType.formatValue(value, payload);
    return [formattedValue, fieldLabel];
  }

  /**
   * Gets special formatter for fields that need custom tooltip formatting
   * 
   * Some fields need special handling beyond standard field type formatting,
   * such as ProCV CO2 sensor which shows air/water mode in tooltips.
   * 
   * @param {string} fieldKey - Field identifier
   * @returns {Function|null} Special formatter function or null
   * @private
   */
  static _getSpecialFormatter(fieldKey) {
    // ProCV CO2 and Mini CO2 sensors share the same Air/Water mode tagging
    const co2ModeFormatter = (value, payload, fieldType) => {
      const mode = payload[`${fieldKey}_mode`];
      const modeLabel = mode === "A M" || mode === "a_m" ? " (Air Mode)" :
                       mode === "W M" || mode === "w_m" ? " (Water Mode)" :
                       mode ? ` (${mode})` : "";

      return `${Number(value).toFixed(fieldType.precision)} ${fieldType.units}${modeLabel}`;
    };

    const specialFormatters = {
      'procv_co2:pCO2': co2ModeFormatter,
      'mini_co2:pCO2': co2ModeFormatter,
    };

    return specialFormatters[fieldKey] || null;
  }

  // _getFieldLabel method removed - using LabelUtils.getFieldLabel instead

  /**
   * Formats timestamp for tooltip display
   * 
   * Converts ISO timestamp to human-readable format with timezone.
   * Handles invalid timestamps gracefully.
   * 
   * @param {string} timestamp - ISO timestamp string
   * @returns {string} Formatted timestamp
   */
  static formatTimestamp(timestamp) {
    if (!timestamp) return 'No timestamp';
    
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'Invalid timestamp';
    
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'UTC',
      timeZoneName: 'short'
    });
  }

  /**
   * Formats multiple values for tooltip display
   * 
   * Used when tooltip shows multiple sensor readings at the same timestamp.
   * Returns array of formatted entries.
   * 
   * @param {Array} entries - Array of {value, fieldKey, payload} objects
   * @returns {Array} Array of formatted tooltip entries
   */
  static formatMultipleValues(entries) {
    return entries.map(entry => {
      const [formattedValue, fieldLabel] = this.formatTooltipValue(
        entry.value, 
        entry.fieldKey, 
        entry.payload
      );
      
      return {
        fieldKey: entry.fieldKey,
        fieldLabel,
        formattedValue,
        color: entry.color
      };
    });
  }

  /**
   * Checks if a value should be flagged as potentially bad data
   * 
   * Identifies values that might be sensor errors or calibration issues.
   * Used to add visual indicators in tooltips.
   * 
   * @param {number} value - Sensor value to check
   * @param {string} fieldKey - Field identifier for context
   * @returns {boolean} True if value seems problematic
   */
  static isValueSuspicious(value, fieldKey) {
    if (typeof value !== 'number' || !isFinite(value)) {
      return true;
    }

    const [measurement, field] = fieldKey.split(':');
    
    // Virtual sensors are exempt from general negative value checks
    // They may legitimately have negative values (temperatures, tide levels, etc.)
    const isVirtualSensor = measurement.startsWith('virtual_');
    
    // Non-virtual sensors: check for negative values (most physical sensors shouldn't be negative)
    if (!isVirtualSensor && value < 0) {
      // Allow some specific fields that can legitimately be negative
      const allowsNegative = [
        'air_temp_c',
        'sea_surface_temp_c', 
        'temperature_c',
        'water_level_m',  // Can be below datum
        'prediction_m'    // Can be below datum
      ];
      
      if (!allowsNegative.some(allowed => field.includes(allowed))) {
        return true;
      }
    }

    // Field-specific suspicious value ranges
    // 
    // CONFIGURATION: Only PCO2 currently has thresholds active
    // To add new sensor thresholds, uncomment and configure the appropriate lines below:
    //
    const suspiciousRanges = {
      'pCO2': { min: 200, max: 2000 },  // Typical seawater pCO2 range in μatm
      
      // Future sensor thresholds - uncomment and adjust ranges as needed:
      // 'salinity_psu': { min: 0, max: 40 },        // Typical seawater salinity
      // 'oxygen_ml_per_l': { min: 0, max: 15 },     // Typical dissolved oxygen
      // 'temperature_c': { min: -5, max: 35 },      // Typical water temperature
      // 'conductivity': { min: 0, max: 60 },        // Typical conductivity range
      // 'pressure': { min: 0, max: 50 },            // Typical pressure range
    };

    for (const [fieldPattern, range] of Object.entries(suspiciousRanges)) {
      if (field.includes(fieldPattern)) {
        if (value < range.min || value > range.max) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Gets appropriate warning message for suspicious values
   * 
   * @param {number} value - The suspicious value
   * @param {string} fieldKey - Field identifier
   * @returns {string} Warning message to display
   */
  static getSuspiciousValueWarning(value, fieldKey) {
    if (value < 0) {
      return "⚠️ Negative value - check sensor calibration";
    }
    
    return "⚠️ Value outside typical range";
  }
}

export default TooltipFormatter;